import React, { FunctionComponent } from 'react';

import { ListItem } from '@yandex-360/orb-components/dist/components/ListItem';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { i18n } from '../../../../../../../helpers/i18n';
import { ACTION, FEATURE } from '../../../../../../../helpers/logger-enums';
import { createLogger } from '../../../../../../../helpers/loggerFacade';
import { isForceDefaultOutput } from '../../../../../../../helpers/media-devices';
import { useActions } from '../../../../../../../hooks';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';
import { selectIsUserIODiagnosticsEnabled } from '../../../../../../flags/selectors';
import {
    getAudioOutputDeviceSelector,
    getDevicesByTypeSelector,
    getIsOutputDeviceChangeAvailable,
    setAudioOutputDeviceAction,
    StoreDevice,
} from '../../../../../../settings';
import { SettingsSelect } from '../../../settings-select';
import { OutputDeviceDiagnosticButton } from '../sound-with-diagnostic/output-device-section/output-device-diagnostic-button';
import styles from './styles.css';

const SELECT_DEVICE_NOT_FOUND_KEY = 'DEVICE_NOT_FOUND';
const SELECT_DEVICE_DEFAULT_KEY = 'DEVICE_DEFAULT';

const logger = createLogger('settings-modal-v2-sound-output');

const getOutputSelectItems = (
    outputDevices: StoreDevice[],
    isForceDefaultDevice: boolean,
    isDevicesNotFound: boolean,
    isDiagnosticAvailable: boolean
) => {
    if (isForceDefaultDevice) {
        return {
            customItemKey: SELECT_DEVICE_DEFAULT_KEY,
            items: [
                {
                    itemKey: SELECT_DEVICE_DEFAULT_KEY,
                    label: i18n('settings-v2', 'default-speaker-device'),
                },
            ],
        };
    }

    if (isDiagnosticAvailable && isDevicesNotFound) {
        return {
            customItemKey: SELECT_DEVICE_NOT_FOUND_KEY,
            items: [
                {
                    itemKey: SELECT_DEVICE_NOT_FOUND_KEY,
                    label: i18n('settings-v2', 'device-is-not-connected'),
                },
            ],
        };
    }

    return {
        items: outputDevices.map(({ deviceId, label }) => ({
            itemKey: deviceId,
            label,
        })),
    };
};
export const OutputDeviceSelect: FunctionComponent = () => {
    const { setAudioOutputDevice } = useActions({ setAudioOutputDevice: setAudioOutputDeviceAction });

    const isDiagnosticAvailable = useSelector(selectIsUserIODiagnosticsEnabled);
    const isOutputDeviceChangeAvailable = useSelector(getIsOutputDeviceChangeAvailable);
    const outputDevices = useTypedSelector((state) => getDevicesByTypeSelector(state, 'audiooutput'));
    const currentOutputDeviceId = useSelector(getAudioOutputDeviceSelector)?.deviceId;

    const isDevicesNotFound = outputDevices.length <= 0;
    const isForceDefault = isForceDefaultOutput(outputDevices.length);
    const isCustomSelectItem = isForceDefault || isDevicesNotFound;

    const { items, customItemKey } = getOutputSelectItems(
        outputDevices,
        isForceDefault,
        isDevicesNotFound,
        isDiagnosticAvailable
    );

    const setDevice = (deviceId: React.Key | null) => {
        logger.startFeatureDelta({
            feature: FEATURE.DEVICES,
            action: ACTION.SELECT_SPEAKER,
        });
        setAudioOutputDevice(String(deviceId));
    };

    if (!isOutputDeviceChangeAvailable || !items.length) {
        return isDiagnosticAvailable ? <OutputDeviceDiagnosticButton /> : null;
    }

    const isChangeDisabled = isDevicesNotFound && !isForceDefault;

    const select = (
        <SettingsSelect
            aria-label={i18n('settings-v2', 'sound-speaker')}
            items={items}
            selectedKey={isCustomSelectItem ? customItemKey : currentOutputDeviceId}
            onSelectionChange={setDevice}
            testId="output-devices-select"
            isDisabled={isChangeDisabled}
            className={styles.selectDevice}
        />
    );
    const diagnosticSelect = (
        <SettingsSelect
            aria-label={i18n('settings-v2', 'sound-speaker')}
            items={items}
            selectedKey={isCustomSelectItem ? customItemKey : currentOutputDeviceId}
            onSelectionChange={isCustomSelectItem ? undefined : setDevice}
            testId="output-devices-select"
            isDisabled={isChangeDisabled}
            className={cn(styles.selectDevice, styles.selectDeviceDiagnostic)}
        />
    );
    return (
        <>
            {isDiagnosticAvailable ? (
                <ListItem
                    label={diagnosticSelect}
                    endSlot={<OutputDeviceDiagnosticButton isDisabled={isChangeDisabled} />}
                    interaction={false}
                />
            ) : (
                <ListItem label={i18n('settings-v2', 'sound-speaker')} endSlot={select} interaction={false} />
            )}
        </>
    );
};
